// ─── eXeX Demo Facility ─────────────────────────────────────────────
// A single synthetic facility that powers a fully self-contained Canopus
// Command demo (no real backend behind it). It is injected ONLY into the
// Canopus facility list — never into the Insights product, which calls
// `insightsService.getCommercialFacilities()` directly.
//
// The demo facility is detected by its fixed id (`DEMO_FACILITY_ID`), which
// is what flips Canopus Command into demo mode. Its display name is editable
// and stored in localStorage so it stays browser-local — concurrent demos on
// the same deployment don't clobber each other's names.

import { insightsService } from '../../../services/insights.js';
import type { FacilityConfig } from '../../../services/missionControl.js';

export const DEMO_FACILITY_ID = 'exex-demo';
export const DEMO_FACILITY_NAME_STORAGE_KEY = 'cc-demo-facility-name';
export const DEFAULT_DEMO_FACILITY_NAME = 'eXeX Demo Facility';

/**
 * Read the browser-local demo facility name. Falls back to the default when
 * unset, blank, or localStorage is unavailable.
 */
export function getDemoFacilityName(): string {
  try {
    const raw = localStorage.getItem(DEMO_FACILITY_NAME_STORAGE_KEY);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : DEFAULT_DEMO_FACILITY_NAME;
  } catch {
    return DEFAULT_DEMO_FACILITY_NAME;
  }
}

/**
 * Persist the demo facility name. A blank/whitespace-only value clears the
 * override, restoring the default. Silently no-ops if localStorage throws.
 */
export function setDemoFacilityName(name: string): void {
  try {
    const trimmed = name.trim();
    if (trimmed) {
      localStorage.setItem(DEMO_FACILITY_NAME_STORAGE_KEY, trimmed);
    } else {
      localStorage.removeItem(DEMO_FACILITY_NAME_STORAGE_KEY);
    }
  } catch {
    // Ignore — the name simply won't persist across reloads.
  }
}

/** True when the given facility (or id) is the synthetic demo facility. */
export function isDemoFacility(facility: FacilityConfig | string | null | undefined): boolean {
  if (!facility) return false;
  const id = typeof facility === 'string' ? facility : facility.id;
  return id === DEMO_FACILITY_ID;
}

/**
 * Build the synthetic demo FacilityConfig. `region: 'Demo'` is deliberate:
 * it does not match `/uk|eu/i` (so the UI shows "OR", matching the baked
 * "OR N" room names) and does not match `/us/i` (so no server-location badge
 * appears next to LIVE — the demo has no real Firebase project behind it).
 */
export function buildDemoFacility(): FacilityConfig {
  return {
    id: DEMO_FACILITY_ID,
    firebaseProject: 'demo',
    facilityDocId: DEMO_FACILITY_ID,
    facilityName: getDemoFacilityName(),
    region: 'Demo',
    facilityType: 'commercial',
    linkedFacilityId: null,
    liveDate: null,
    location: 'Demo Environment',
  };
}

/**
 * Canopus-scoped facility list: the demo facility first, then the real
 * commercial facilities. Returns a fresh array so the array the Insights
 * product receives from `insightsService.getCommercialFacilities()` is never
 * mutated.
 *
 * If the commercial facilities request fails, still return the demo facility
 * alone — it has no backend dependency and should remain reachable for demos.
 */
export async function getCanopusFacilities(): Promise<FacilityConfig[]> {
  const demo = buildDemoFacility();
  try {
    const real = await insightsService.getCommercialFacilities();
    return [demo, ...real];
  } catch {
    return [demo];
  }
}
